import PosCartSummary from "@/Components/PosCart/PosCartSummary";
import PosCart from "@/Components/PosCart/index";
import PosMenu from "@/Components/PosMenu";
import Spinner from "@/Components/Spinner/Spinner";
import useMobileCartShow from "@/shared/atoms/useMobileCartShow";
import usePosShowOrders, {
  useSetPosShowOrders,
} from "@/shared/atoms/usePosShowOrders";
import useIsMobileHook from "@/shared/hooks/useIsMobileHook";
import useIsTabletHook from "@/shared/hooks/useIsTabletHook";
import usePosSettingsHook from "@/shared/hooks/usePosSettingsHook";
import { Fragment, useEffect } from "react";
import { createPortal } from "react-dom";
import PosOrders from "./PosOrders";
import Settings from "./Settings";
import PosOrderUserSettings from "./PosOrderUserSettings";
import usePosShowSettings from "@/shared/atoms/usePosShowSettings";
import useFullScreen from "@/shared/atoms/useFullScreen";
import PosGlobalReset from "./PosGlobalReset";
import PosGlobal from "./PosGlobal";
import PosGlobalBarcode from "./PosGlobalBarcode";
import usePosOrderCartWidthAtom from "@/shared/atoms/usePosOrderCartWidthAtom";
import { cn } from "@/shared/utils/cn";
import useIsWaiter from "@/shared/hooks/useIsWaiter";
import useLocationHook from "@/shared/hooks/useLocationHook";
import usePosOrderTable from "@/shared/atoms/usePosOrderTable";
import useWaiterView from "@/shared/atoms/useWaiterView";
import useOrdersSessionByTableQuery from "@/shared/queries/orderssessions/useOrdersSessionByTableQuery";

const PosSystem = () => {
  const isMobile = useIsMobileHook();
  const isTablet = useIsTabletHook();
  const [showCartForMobile, setShowCartForMobile] = useMobileCartShow();
  const { isPending, isSuccess, settings } = usePosSettingsHook();
  const [showPosOrders] = usePosShowOrders();
  const setShowPosOrders = useSetPosShowOrders();
  const [showPosSettings] = usePosShowSettings();
  const [fullScreen] = useFullScreen();
  const [posOrderCartWidth] = usePosOrderCartWidthAtom();
  const isWaiter = useIsWaiter();
  const { isWaiterPage } = useLocationHook();
  const [waiterView] = useWaiterView();
  const hideCartOnTableSelect = isWaiter && waiterView === "tables";
  const hideCart = hideCartOnTableSelect || (isWaiter && showPosSettings);
  useOrdersSessionByTableQuery();
  const showMobileCart =
    (isMobile || isTablet) && showCartForMobile && !hideCart;

  useEffect(() => {
    if (!isWaiterPage) return;
    setShowPosOrders(false);
    setShowCartForMobile(false);
  }, [isWaiterPage, setShowPosOrders, setShowCartForMobile]);

  useEffect(() => {
    if (hideCart) {
      setShowCartForMobile(false);
    }
  }, [hideCart, setShowCartForMobile]);

  const PosChildren = showPosSettings
    ? isWaiterPage
      ? PosOrderUserSettings
      : Settings
    : isWaiterPage
      ? PosMenu
      : showPosOrders
        ? PosOrders
        : PosMenu;

  const PosChildrenComponent = !showMobileCart && (
    <div
      className={cn(
        "relative flex h-auto w-full flex-col overflow-y-auto",
        fullScreen ? "md:h-[calc(100vh-3rem)]" : "md:h-[calc(100vh-10rem)]",
        isMobile || isTablet || hideCart
          ? "w-full"
          : `w-[calc(100% - ${posOrderCartWidth}%)]`
      )}
    >
      <PosChildren>
        {(isMobile || isTablet) &&
          !showMobileCart &&
          !hideCart &&
          createPortal(
            <PosCartSummary
              onClickShowCart={() => setShowCartForMobile(true)}
            />,
            document.body
          )}
      </PosChildren>
    </div>
  );

  const PosCartComponent = !hideCart &&
    (!(isMobile || isTablet) || showMobileCart) && (
      <div
        className={cn(
          isMobile || isTablet ? "w-full" : `w-[${posOrderCartWidth}%]`,
          isWaiter && "h-full"
        )}
      >
        <PosCart onClickBack={() => setShowCartForMobile(false)} />
      </div>
    );

  return (
    <div
      className={cn(
        "flex w-full gap-4 overflow-hidden",
        isWaiter && "flex h-full min-h-0 flex-1"
      )}
    >
      {isPending ? (
        <Spinner />
      ) : isSuccess ? (
        <Fragment>
          {settings.is_cart_on_right
            ? [PosChildrenComponent, PosCartComponent].filter(Boolean)
            : [PosCartComponent, PosChildrenComponent].filter(Boolean)}
          <>
            <PosGlobalReset />
            <PosGlobal />
            <PosGlobalBarcode />
          </>
        </Fragment>
      ) : null}
    </div>
  );
};

export default PosSystem;
